import React, { createContext, useContext, useState, useEffect } from 'react';
import { formatCurrency, formatCurrencyNumber, parseCurrency } from '../utils/currencyFormatter';
import { formatDate, formatTime, formatDateTime } from '../utils/dateUtils';
import apiService from '../services/api';

const BusinessSettingsContext = createContext(null);

export const BusinessSettingsProvider = ({ children }) => {
  // Read dynamic business settings from localStorage with safe defaults
  const [settings, setSettings] = useState({
    currencySymbol: localStorage.getItem('etpos_currency_symbol') || '$',
    currencyPlacement: localStorage.getItem('etpos_currency_placement') || 'before',
    currencyPrecision: parseInt(localStorage.getItem('etpos_currency_precision') || '2', 10),
    decimalSeparator: localStorage.getItem('etpos_decimal_separator') || '.',
    thousandSeparator: localStorage.getItem('etpos_thousand_separator') || ',',
    dateFormat: localStorage.getItem('etpos_date_format') || 'm/d/Y',
    timeFormat: localStorage.getItem('etpos_time_format') || '12',
    timezone: localStorage.getItem('etpos_business_timezone') || 'America/Chicago',
    quantityPrecision: parseInt(localStorage.getItem('etpos_quantity_precision') || '2', 10),
    defaultProfitPercent: localStorage.getItem('etpos_default_profit_percent') || '25',
    enableBrand: localStorage.getItem('etpos_enable_brand') !== '0',
    enableCategory: localStorage.getItem('etpos_enable_category') !== '0',
    enableSubCategory: localStorage.getItem('etpos_enable_sub_category') !== '0',
    enablePriceTax: localStorage.getItem('etpos_enable_price_tax') !== '0',
    enableSubUnits: localStorage.getItem('etpos_enable_sub_units') !== '0',
    enableSecondaryUnit: localStorage.getItem('etpos_enable_secondary_unit') !== '0',
    enableProductWarranty: localStorage.getItem('etpos_enable_product_warranty') !== '0',
    isProductImageRequired: localStorage.getItem('etpos_is_product_image_required') === '1',
    enableProductImageInList: localStorage.getItem('etpos_enable_product_image_in_list') !== '0',
    enableRacks: localStorage.getItem('etpos_enable_racks') === '1',
    enableRow: localStorage.getItem('etpos_enable_row') === '1',
    enablePosition: localStorage.getItem('etpos_enable_position') === '1',
    enableProductExpiry: localStorage.getItem('etpos_enable_product_expiry') === '1',
    skuPrefix: localStorage.getItem('etpos_sku_prefix') || '',
    defaultUnit: localStorage.getItem('etpos_default_unit') || '',
    salesCommissionAgent: localStorage.getItem('etpos_sales_cmsn_agnt') || '',
    isPayTermRequired: localStorage.getItem('etpos_is_pay_term_required') === '1',
    isCommissionAgentRequired: localStorage.getItem('etpos_is_commission_agent_required') === '1',
    allowOverselling: localStorage.getItem('etpos_allow_overselling') !== '0',
    enableMsp: localStorage.getItem('etpos_enable_msp') === '1',
    defaultSalesDiscount: localStorage.getItem('etpos_default_sales_discount') || '0.00',
    defaultSalesTax: localStorage.getItem('etpos_default_sales_tax') || '',
    enableSalesOrder: localStorage.getItem('etpos_enable_sales_order') === '1',
    enableEditingProductFromPurchase: localStorage.getItem('etpos_enable_editing_product_from_purchase') === '1',
    enablePurchaseStatus: localStorage.getItem('etpos_enable_purchase_status') === '1',
    enableLotNumber: localStorage.getItem('etpos_enable_lot_number') === '1',
    enablePurchaseOrder: localStorage.getItem('etpos_enable_purchase_order') === '1',
    enablePurchaseRequisition: localStorage.getItem('etpos_enable_purchase_requisition') === '1',
  });

  const reloadSettings = () => {
    setSettings({
      currencySymbol: localStorage.getItem('etpos_currency_symbol') || '$',
      currencyPlacement: localStorage.getItem('etpos_currency_placement') || 'before',
      currencyPrecision: parseInt(localStorage.getItem('etpos_currency_precision') || '2', 10),
      decimalSeparator: localStorage.getItem('etpos_decimal_separator') || '.',
      thousandSeparator: localStorage.getItem('etpos_thousand_separator') || ',',
      dateFormat: localStorage.getItem('etpos_date_format') || 'm/d/Y',
      timeFormat: localStorage.getItem('etpos_time_format') || '12',
      timezone: localStorage.getItem('etpos_business_timezone') || 'America/Chicago',
      quantityPrecision: parseInt(localStorage.getItem('etpos_quantity_precision') || '2', 10),
      defaultProfitPercent: localStorage.getItem('etpos_default_profit_percent') || '25',
      enableBrand: localStorage.getItem('etpos_enable_brand') !== '0',
      enableCategory: localStorage.getItem('etpos_enable_category') !== '0',
      enableSubCategory: localStorage.getItem('etpos_enable_sub_category') !== '0',
      enablePriceTax: localStorage.getItem('etpos_enable_price_tax') !== '0',
      enableSubUnits: localStorage.getItem('etpos_enable_sub_units') !== '0',
      enableSecondaryUnit: localStorage.getItem('etpos_enable_secondary_unit') !== '0',
      enableProductWarranty: localStorage.getItem('etpos_enable_product_warranty') !== '0',
      isProductImageRequired: localStorage.getItem('etpos_is_product_image_required') === '1',
      enableProductImageInList: localStorage.getItem('etpos_enable_product_image_in_list') !== '0',
      enableRacks: localStorage.getItem('etpos_enable_racks') === '1',
      enableRow: localStorage.getItem('etpos_enable_row') === '1',
      enablePosition: localStorage.getItem('etpos_enable_position') === '1',
      enableProductExpiry: localStorage.getItem('etpos_enable_product_expiry') === '1',
      skuPrefix: localStorage.getItem('etpos_sku_prefix') || '',
      defaultUnit: localStorage.getItem('etpos_default_unit') || '',
      salesCommissionAgent: localStorage.getItem('etpos_sales_cmsn_agnt') || '',
      isPayTermRequired: localStorage.getItem('etpos_is_pay_term_required') === '1',
      isCommissionAgentRequired: localStorage.getItem('etpos_is_commission_agent_required') === '1',
      allowOverselling: localStorage.getItem('etpos_allow_overselling') !== '0',
      enableMsp: localStorage.getItem('etpos_enable_msp') === '1',
      defaultSalesDiscount: localStorage.getItem('etpos_default_sales_discount') || '0.00',
      defaultSalesTax: localStorage.getItem('etpos_default_sales_tax') || '',
      enableSalesOrder: localStorage.getItem('etpos_enable_sales_order') === '1',
      enableEditingProductFromPurchase: localStorage.getItem('etpos_enable_editing_product_from_purchase') === '1',
      enablePurchaseStatus: localStorage.getItem('etpos_enable_purchase_status') === '1',
      enableLotNumber: localStorage.getItem('etpos_enable_lot_number') === '1',
      enablePurchaseOrder: localStorage.getItem('etpos_enable_purchase_order') === '1',
      enablePurchaseRequisition: localStorage.getItem('etpos_enable_purchase_requisition') === '1',
    });
  };

  // Sync settings when storage updates in any tab/window
  const syncBusinessSettings = async () => {
    const token = localStorage.getItem('etpos_token');
    if (!token) return;

    try {
      const tzData = await apiService.getBusinessDetails();
      const business = tzData.data?.business || tzData.data || tzData;

      const timeZone = business.time_zone || business.timezone || business.timeZone;
      if (timeZone) localStorage.setItem('etpos_business_timezone', timeZone);

      const dateFormat = business.date_format || business.dateFormat;
      if (dateFormat) localStorage.setItem('etpos_date_format', dateFormat);

      const timeFormat = business.time_format || business.timeFormat;
      if (timeFormat) localStorage.setItem('etpos_time_format', String(timeFormat));

      if (business.default_profit_percent !== undefined && business.default_profit_percent !== null) {
        localStorage.setItem('etpos_default_profit_percent', String(business.default_profit_percent));
      }

      if (business.enable_brand !== undefined) localStorage.setItem('etpos_enable_brand', String(business.enable_brand));
      if (business.enable_category !== undefined) localStorage.setItem('etpos_enable_category', String(business.enable_category));
      if (business.enable_sub_category !== undefined) localStorage.setItem('etpos_enable_sub_category', String(business.enable_sub_category));
      if (business.enable_price_tax !== undefined) localStorage.setItem('etpos_enable_price_tax', String(business.enable_price_tax));
      if (business.enable_sub_units !== undefined) localStorage.setItem('etpos_enable_sub_units', String(business.enable_sub_units));
      if (business.enable_racks !== undefined) localStorage.setItem('etpos_enable_racks', String(business.enable_racks));
      if (business.enable_row !== undefined) localStorage.setItem('etpos_enable_row', String(business.enable_row));
      if (business.enable_position !== undefined) localStorage.setItem('etpos_enable_position', String(business.enable_position));
      if (business.enable_product_expiry !== undefined) localStorage.setItem('etpos_enable_product_expiry', String(business.enable_product_expiry));
      if (business.sku_prefix !== undefined) localStorage.setItem('etpos_sku_prefix', String(business.sku_prefix || ''));
      if (business.default_unit !== undefined) localStorage.setItem('etpos_default_unit', String(business.default_unit || ''));

      let common = business.common_settings || {};
      if (typeof common === 'string') {
        try { common = JSON.parse(common); } catch (e) { common = {}; }
      }

      const secondaryUnitVal = common.enable_secondary_unit ?? business.enable_secondary_unit;
      if (secondaryUnitVal !== undefined) localStorage.setItem('etpos_enable_secondary_unit', String(secondaryUnitVal));

      const warrantyVal = common.enable_product_warranty ?? common.enable_warranty ?? business.enable_product_warranty ?? business.enable_warranty;
      if (warrantyVal !== undefined) localStorage.setItem('etpos_enable_product_warranty', String(warrantyVal));

      const imageReqVal = common.is_product_image_required ?? business.is_product_image_required;
      if (imageReqVal !== undefined) localStorage.setItem('etpos_is_product_image_required', String(imageReqVal));

      const imageInListVal = common.enable_product_image_in_list ?? business.enable_product_image_in_list;
      if (imageInListVal !== undefined) localStorage.setItem('etpos_enable_product_image_in_list', String(imageInListVal));

      if (business.enable_editing_product_from_purchase !== undefined) {
        localStorage.setItem('etpos_enable_editing_product_from_purchase', String(business.enable_editing_product_from_purchase));
      }
      if (business.enable_purchase_status !== undefined) {
        localStorage.setItem('etpos_enable_purchase_status', String(business.enable_purchase_status));
      }
      if (business.enable_lot_number !== undefined) {
        localStorage.setItem('etpos_enable_lot_number', String(business.enable_lot_number));
      }
      if (common.enable_purchase_order !== undefined) {
        localStorage.setItem('etpos_enable_purchase_order', String(common.enable_purchase_order));
      }
      if (common.enable_purchase_requisition !== undefined) {
        localStorage.setItem('etpos_enable_purchase_requisition', String(common.enable_purchase_requisition));
      }

      if (business.currency_precision !== undefined && business.currency_precision !== null) {
        localStorage.setItem('etpos_currency_precision', String(business.currency_precision));
      }
      if (business.quantity_precision !== undefined && business.quantity_precision !== null) {
        localStorage.setItem('etpos_quantity_precision', String(business.quantity_precision));
      }
      if (business.currency_symbol_placement) {
        localStorage.setItem('etpos_currency_placement', business.currency_symbol_placement);
      }

      let posSettings = tzData.data?.pos_settings || business.pos_settings || {};
      if (typeof posSettings === 'string') {
        try { posSettings = JSON.parse(posSettings); } catch (e) { posSettings = {}; }
      }

      if (posSettings && Object.keys(posSettings).length > 0) {
        localStorage.setItem('etpos_pos_settings', JSON.stringify(posSettings));
      }

      let shortcuts = tzData.data?.shortcuts || business.shortcuts || {};
      if (typeof shortcuts === 'string') {
        try { shortcuts = JSON.parse(shortcuts); } catch (e) { shortcuts = {}; }
      }
      if (shortcuts && Object.keys(shortcuts).length > 0) {
        localStorage.setItem('etpos_shortcuts', JSON.stringify(shortcuts));
      }

      let weighingScale = tzData.data?.weighing_scale_setting || posSettings.weighing_scale_setting || business.weighing_scale_setting || {};
      if (typeof weighingScale === 'string') {
        try { weighingScale = JSON.parse(weighingScale); } catch (e) { weighingScale = {}; }
      }
      if (weighingScale && Object.keys(weighingScale).length > 0) {
        localStorage.setItem('etpos_weighing_scale_setting', JSON.stringify(weighingScale));
      }

      if (business.sales_cmsn_agnt !== undefined) localStorage.setItem('etpos_sales_cmsn_agnt', String(business.sales_cmsn_agnt || ''));
      const isPayTermReq = posSettings.is_pay_term_required ?? common.is_pay_term_required ?? business.is_pay_term_required;
      if (isPayTermReq !== undefined) {
        localStorage.setItem('etpos_is_pay_term_required', (isPayTermReq == 1 || isPayTermReq === true || isPayTermReq === '1') ? '1' : '0');
      }

      const isCmsnReq = posSettings.is_commission_agent_required ?? common.is_commission_agent_required ?? business.is_commission_agent_required;
      if (isCmsnReq !== undefined) {
        localStorage.setItem('etpos_is_commission_agent_required', (isCmsnReq == 1 || isCmsnReq === true || isCmsnReq === '1') ? '1' : '0');
      }
      if (posSettings.allow_overselling !== undefined) localStorage.setItem('etpos_allow_overselling', String(posSettings.allow_overselling ? '1' : '0'));
      if (posSettings.enable_msp !== undefined) localStorage.setItem('etpos_enable_msp', String(posSettings.enable_msp ? '1' : '0'));
      if (business.default_sales_discount !== undefined) localStorage.setItem('etpos_default_sales_discount', String(business.default_sales_discount || '0.00'));
      if (business.default_sales_tax !== undefined) localStorage.setItem('etpos_default_sales_tax', String(business.default_sales_tax || ''));
      if (posSettings.enable_sales_order !== undefined) {
        localStorage.setItem('etpos_enable_sales_order', String(posSettings.enable_sales_order ? '1' : '0'));
      }

      const currency = business.currency || tzData.currency || tzData.data?.currency;
      // Only write currency values when we actually have them — never overwrite with a '$' default
      const symbol = currency?.symbol || business.currency_symbol || business.currencySymbol;
      if (symbol) localStorage.setItem('etpos_currency_symbol', symbol);

      const decimalSep = currency?.decimal_separator || business.decimal_separator;
      if (decimalSep) localStorage.setItem('etpos_decimal_separator', decimalSep);

      const thousandSep = currency?.thousand_separator || business.thousand_separator;
      if (thousandSep) localStorage.setItem('etpos_thousand_separator', thousandSep);

      reloadSettings();
    } catch (e) {
      /* console.error('Failed to sync business settings:', e); */
    }
  };

  // Sync settings when storage updates or component mounts
  useEffect(() => {
    syncBusinessSettings();

    const handleStorageChange = () => {
      syncBusinessSettings();
    };
    window.addEventListener('etpos-settings-update', handleStorageChange);
    return () => {
      window.removeEventListener('etpos-settings-update', handleStorageChange);
    };
  }, []);

  const contextValue = {
    settings,
    reloadSettings,
    syncBusinessSettings,
    formatCurrency: (amount) => formatCurrency(amount),
    formatCurrencyNumber: (amount) => formatCurrencyNumber(amount),
    parseCurrency: (formattedAmount) => parseCurrency(formattedAmount),
    formatDate: (date) => formatDate(date),
    formatTime: (date) => formatTime(date),
    formatDateTime: (date) => formatDateTime(date),
    currencyPrecision: settings.currencyPrecision,
    quantityPrecision: settings.quantityPrecision,
    defaultProfitPercent: settings.defaultProfitPercent,
    ...settings,
  };

  return (
    <BusinessSettingsContext.Provider value={contextValue}>
      {children}
    </BusinessSettingsContext.Provider>
  );
};

export const useBusinessSettings = () => {
  const context = useContext(BusinessSettingsContext);
  if (!context) {
    throw new Error('useBusinessSettings must be used within a BusinessSettingsProvider');
  }
  return context;
};

export default BusinessSettingsContext;
